'use client';

import { useEffect, useMemo, useState } from 'react';
import { PermissionGuard } from '@/components/common/PermissionGuard';
import { useTranslation } from '@/hooks/useTranslation';
import { SYSTEM_ADMIN_PERMISSION } from '@/lib/iam-constants';
import { UserSelector, type UserInfo } from '@/components/UserSelector';
import { getUserById } from '@/services/api/organization';
import { toast } from '@/lib/toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  listMyDelegations,
  createDelegation,
  revokeDelegation,
  type PermissionDelegation,
} from '@/services/api/iam-governance';

/**
 * 权限委托管理页面（规则 §5.3.14）
 *
 * 权限：delegation:manage 或 system:admin
 */
export default function DelegationsPage() {
  return (
    <PermissionGuard permissions={['delegation:manage', SYSTEM_ADMIN_PERMISSION]}>
      <DelegationsContent />
    </PermissionGuard>
  );
}

type Tab = 'sent' | 'received' | 'all';

function DelegationsContent() {
  const { t } = useTranslation();
  const [list, setList] = useState<PermissionDelegation[]>([]);
  const [userMap, setUserMap] = useState<Record<string, UserInfo>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [tab, setTab] = useState<Tab>('all');
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    // 从 localStorage 拿当前 userId（用于切 sent / received tab）
    try {
      const auth = JSON.parse(localStorage.getItem('auth-storage') || '{}');
      setCurrentUserId(auth?.state?.user?.id || '');
    } catch {
      // ignore
    }
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      const data = (await listMyDelegations()) ?? [];
      setList(data);
      // 批量取所有涉及的 user 信息
      const ids = new Set<string>();
      data.forEach((d) => {
        ids.add(d.fromUserId);
        ids.add(d.toUserId);
      });
      await fetchUsers(Array.from(ids));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t.iamGovernance.delegations.loadFailed);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUsers(ids: string[]) {
    const missing = ids.filter((id) => !userMap[id]);
    if (missing.length === 0) return;
    const results = await Promise.all(
      missing.map((id) =>
        getUserById(id)
          .then((u: any) => [id, { id: u.id, displayName: u.displayName, username: u.username, email: u.email }] as const)
          .catch(() => [id, { id, displayName: id.slice(0, 8) }] as const),
      ),
    );
    setUserMap((prev) => {
      const next = { ...prev };
      for (const [id, info] of results) next[id] = info as UserInfo;
      return next;
    });
  }

  const filteredList = useMemo(() => {
    if (tab === 'sent') return list.filter((d) => d.fromUserId === currentUserId);
    if (tab === 'received') return list.filter((d) => d.toUserId === currentUserId);
    return list;
  }, [list, tab, currentUserId]);

  async function handleRevoke(id: string) {
    if (!confirm(t.iamGovernance.delegations.revokeConfirm)) return;
    try {
      await revokeDelegation(id);
      toast.success(t.common?.success ?? 'Success');
      await loadData();
    } catch (err: unknown) {
      toast.error(
        t.iamGovernance.delegations.revokeFailed +
          (err instanceof Error ? err.message : ''),
      );
    }
  }

  function renderUser(id: string) {
    const u = userMap[id];
    if (!u) return <span className="font-mono text-xs text-gray-400">{id.slice(0, 8)}…</span>;
    return (
      <div className="flex flex-col">
        <span className="text-sm">{u.displayName || u.username || id.slice(0, 8)}</span>
        {u.email && <span className="text-xs text-gray-500">{u.email}</span>}
      </div>
    );
  }

  function renderStatus(d: PermissionDelegation) {
    if (d.revokedAt) {
      return (
        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
          {t.iamGovernance.delegations.status.revoked}
        </span>
      );
    }
    if (new Date(d.validTo) < new Date()) {
      return (
        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
          {t.iamGovernance.delegations.status.expired}
        </span>
      );
    }
    return (
      <span className="rounded bg-green-50 px-2 py-0.5 text-xs text-green-700">
        {t.iamGovernance.delegations.status.active}
      </span>
    );
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'all', label: t.iamGovernance.delegations.tabs.all },
    { key: 'sent', label: t.iamGovernance.delegations.tabs.sent },
    { key: 'received', label: t.iamGovernance.delegations.tabs.received },
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t.iamGovernance.delegations.title}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t.iamGovernance.delegations.subtitle}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          {t.iamGovernance.delegations.newButton}
        </Button>
      </div>

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {tabs.map((it) => (
          <button
            key={it.key}
            onClick={() => setTab(it.key)}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${
              tab === it.key
                ? 'border-blue-600 text-blue-600 font-medium'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {it.label}
          </button>
        ))}
      </div>

      {loading && <div className="py-12 text-center text-gray-400">{t.iamGovernance.delegations.loading}</div>}
      {error && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-600">{error}</div>
      )}

      {!loading && !error && (
        <div className="rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">{t.iamGovernance.delegations.columns.from}</th>
                <th className="px-4 py-3 text-left">{t.iamGovernance.delegations.columns.to}</th>
                <th className="px-4 py-3 text-left">{t.iamGovernance.delegations.columns.resource}</th>
                <th className="px-4 py-3 text-left">{t.iamGovernance.delegations.columns.valid}</th>
                <th className="px-4 py-3 text-left">{t.iamGovernance.delegations.columns.reason}</th>
                <th className="px-4 py-3 text-left">{t.iamGovernance.delegations.columns.status}</th>
                <th className="px-4 py-3 text-right">{t.iamGovernance.delegations.columns.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredList.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    {t.iamGovernance.delegations.empty}
                  </td>
                </tr>
              )}
              {filteredList.map((d) => (
                <tr key={d.id}>
                  <td className="px-4 py-3">{renderUser(d.fromUserId)}</td>
                  <td className="px-4 py-3">{renderUser(d.toUserId)}</td>
                  <td className="px-4 py-3">{d.resource}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {new Date(d.validFrom).toLocaleDateString()} ~{' '}
                    {new Date(d.validTo).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{d.reason}</td>
                  <td className="px-4 py-3">{renderStatus(d)}</td>
                  <td className="px-4 py-3 text-right">
                    {!d.revokedAt && new Date(d.validTo) > new Date() && (
                      <button
                        onClick={() => handleRevoke(d.id)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        {t.iamGovernance.delegations.revoke}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateDelegationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={loadData}
      />
    </div>
  );
}

function CreateDelegationDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [toUser, setToUser] = useState<UserInfo | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [resource, setResource] = useState('*');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setToUser(null);
    setResource('*');
    setValidFrom('');
    setValidTo('');
    setReason('');
  }

  async function submit() {
    if (!toUser || !validFrom || !validTo || !reason.trim()) {
      toast.error(t.iamGovernance.delegations.form.requiredAll);
      return;
    }
    try {
      setSubmitting(true);
      await createDelegation({
        toUserId: toUser.id,
        resource: resource || '*',
        validFrom: new Date(validFrom).toISOString(),
        validTo: new Date(validTo).toISOString(),
        reason,
      });
      toast.success(t.common?.success ?? 'Success');
      reset();
      onOpenChange(false);
      onCreated();
    } catch (err: unknown) {
      toast.error(
        t.iamGovernance.delegations.form.createFailed +
          (err instanceof Error ? err.message : ''),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{t.iamGovernance.delegations.form.title}</DialogTitle>
            <DialogDescription>
              {t.iamGovernance.delegations.subtitle}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t.iamGovernance.delegations.form.toUser}</Label>
              <div className="flex items-center gap-2">
                <div className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm">
                  {toUser ? (
                    <span>
                      {toUser.displayName}
                      {toUser.email && <span className="ml-2 text-xs text-gray-500">{toUser.email}</span>}
                    </span>
                  ) : (
                    <span className="text-gray-400">{t.iamGovernance.delegations.form.toUserPlaceholder}</span>
                  )}
                </div>
                <Button variant="outline" type="button" onClick={() => setPickerOpen(true)}>
                  {t.iamGovernance.delegations.form.pickUser}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t.iamGovernance.delegations.form.resource}</Label>
              <Input
                value={resource}
                onChange={(e) => setResource(e.target.value)}
                placeholder="*"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t.iamGovernance.delegations.form.validFrom}</Label>
                <Input
                  type="datetime-local"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.iamGovernance.delegations.form.validTo}</Label>
                <Input
                  type="datetime-local"
                  value={validTo}
                  onChange={(e) => setValidTo(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t.iamGovernance.delegations.form.reason}</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t.iamGovernance.delegations.form.reasonPlaceholder}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {t.iamGovernance.delegations.form.cancel}
            </Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting
                ? t.iamGovernance.delegations.form.submitting
                : t.iamGovernance.delegations.form.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserSelector
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={(users) => {
          if (users.length > 0) setToUser(users[0]);
          setPickerOpen(false);
        }}
        multiple={false}
        title={t.iamGovernance.delegations.form.pickUserTitle}
      />
    </>
  );
}
